import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type Note } from '../lib/api.ts'
import { 
  Search, 
  Plus, 
  FileText, 
  Clock, 
  Trash2, 
  Sparkles,
  Loader2,
  X
} from 'lucide-react'

function Notes() {
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [semanticSearch, setSemanticSearch] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const navigate = useNavigate()

  const fetchNotes = useCallback(async () => {
    try {
      const data = await api.getNotes()
      setNotes(data)
    } catch (err) {
      console.error('Failed to fetch notes:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNotes()
  }, [fetchNotes])

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) {
      fetchNotes()
      return
    }

    setSearching(true)
    try {
      const results = await api.searchNotes(searchQuery, semanticSearch)
      setNotes(results)
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    fetchNotes()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return
    
    setDeletingId(id)
    try {
      await api.deleteNote(id)
      setNotes(notes.filter(n => n.id !== id))
    } catch (err) {
      console.error('Delete failed:', err)
      alert('Failed to delete note')
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Notes</h1>
          <p className="text-gray-600 mt-1">
            {notes.length} {notes.length === 1 ? 'note' : 'notes'}
          </p>
        </div>
        
        <Link
          to="/notes/new"
          className="btn-primary self-start"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Note
        </Link>
      </div>

      {/* Search */}
      <div className="mb-8">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes..."
              className="input pl-10 w-full"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <button
            type="submit"
            disabled={searching}
            className="btn-secondary"
          >
            {searching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Search'
            )}
          </button>
        </form>
        
        {/* Semantic search toggle */}
        <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={semanticSearch}
            onChange={(e) => setSemanticSearch(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <Sparkles className="w-4 h-4" />
          <span>AI-powered semantic search</span>
        </label>
      </div>

      {/* Notes grid */}
      {notes.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? 'No notes found' : 'No notes yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchQuery 
              ? 'Try adjusting your search terms' 
              : 'Create your first note to get started'}
          </p>
          {!searchQuery && (
            <Link to="/notes/new" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Note
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="card hover:shadow-md transition-shadow group"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 
                    className="font-semibold text-gray-900 line-clamp-1 cursor-pointer"
                    onClick={() => navigate(`/notes/${note.id}`)}
                  >
                    {note.title}
                  </h3>
                  <button
                    onClick={() => handleDelete(note.id)}
                    disabled={deletingId === note.id}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity p-1"
                    title="Delete note"
                  >
                    {deletingId === note.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
                
                {note.summary ? (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                    {note.summary}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                    {note.content || 'No content'}
                  </p>
                )}
                
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatDate(note.updated_at)}
                  {note.summary && (
                    <span className="ml-2 flex items-center text-primary-600">
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI summary
                    </span>
                  )}
                </div>
              </div>
              
              <div 
                className="px-6 py-3 bg-gray-50 border-t border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => navigate(`/notes/${note.id}`)}
              >
                <span className="text-sm text-primary-600 font-medium">
                  Open note →
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Notes
